import { ThemeOptions } from "@mui/material";
import palette from './palette.module.scss';

import './theme.modules';

import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/700.css';

const defaultFontFamily = [
  '"Poppins"'
].join(',');
const headerFontFamily = defaultFontFamily.slice(0);

export const baseTheme: ThemeOptions = {
  typography: {
    fontFamily: defaultFontFamily,
    h1: {
      fontFamily: headerFontFamily
    },
    h2: {
      fontFamily: headerFontFamily
    },
    h3: {
      fontFamily: headerFontFamily
    },
    h4: {
      fontFamily: headerFontFamily
    },
    h5: {
      fontFamily: headerFontFamily
    },
    h6: {
      fontFamily: headerFontFamily
    },
    button: {
      fontFamily: defaultFontFamily,
      fontWeight: '700',
      fontSize: '15px',
      lineHeight: '20px',
      textTransform: 'none'
    }
  },
  components: {
      MuiButton: {
          styleOverrides: {
              root: {
                  borderRadius: '6px',
                  padding: '18px 24px',
                  transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, font-size 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, font-weight 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, padding 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden'
              }
          },
          defaultProps: {
              variant: 'contained'
          },
          variants: [
              {
                  props: { size: 'small' },
                  style: {
                      padding: '10px 15px'
                  }
              },
              {
                  props: { size: 'large' },
                  style: {
                      fontWeight: '800',
                      fontSize: '18px'
                  }
              }
          ]
      },
      MuiCard: {
          styleOverrides: {
              root: {
                  borderRadius: '10px',
                  padding: '20px'
              }
          },
          variants: [
              {
                  props: { color: 'primary' },
                  style: { backgroundColor: 'var(--color-primary)' }
              },
              {
                  props: { color: 'secondary' },
                  style: { backgroundColor: 'var(--color-secondary)' }
              },
              {
                  props: { color: 'success' },
                  style: { backgroundColor: 'var(--color-success)' }
              },
              {
                  props: { color: 'warning' },
                  style: { backgroundColor: 'var(--color-warning)' }
              },
              {
                  props: { color: 'error' },
                  style: { backgroundColor: 'var(--color-error)' }
              },
              {
                  props: { color: 'tertiary' },
                  style: { backgroundColor: 'var(--color-tertiary)' }
              }
          ]
      },
      MuiAccordion: {
          styleOverrides: {
              root: {
                  borderRadius: '10px !important',
                  padding: '20px'
              }
          }
      },
      MuiAccordionSummary: {
          styleOverrides: {
              root: {
                  padding: '0px 16px'
              }
          }
      },
      MuiAccordionDetails: {
          styleOverrides: {
              root: {
                  padding: '8px 16px 16px 16px'
              }
          }
      },
      MuiAccordionActions: {
          styleOverrides: {
              root: {
                  padding: '8px 36px'
              }
          }
      },
      MuiCardActions: {
          styleOverrides: {
              root: {
                  padding: '16px'
              }
          }
      },
      MuiTextField: {
          defaultProps: {
              fullWidth: true,
              margin: 'normal'
          },
          variants: [
              {
                  props: { size: 'small' },
                  style: {
                      fontSize: '14px'
                  }
              }
          ]
      },
      MuiInput: {
          styleOverrides: {
              root: {
                  borderRadius: '10px'
              }
          },
          variants: [
              {
                  props: { size: 'small' },
                  style: {
                      fontSize: '14px'
                  }
              }
          ]
      },
      MuiOutlinedInput: {
          styleOverrides: {
              root: {
                  borderRadius: '10px'
              }
          },
          variants: [
              {
                  props: { size: 'small' },
                  style: {
                      fontSize: '14px'
                  }
              }
          ]
      },
      MuiAlert: {
          styleOverrides: {
              root: {
                  width: '100%',
                  boxSizing: 'border-box'
              },
              message: {
                  flex: '1'
              }
          }
      },
      MuiPaper: {
          styleOverrides: {
              root: {
                  borderRadius: '10px',
                  flex: '1'
              }
          }
      },
      MuiFormControl: {
          defaultProps: {
              margin: 'normal'
          }
      },
      MuiMenu: {
          styleOverrides: {
              root: {
                  maxHeight: '250px'
              }
          }
      },
      MuiMenuItem: {
          styleOverrides: {
              root: {
                  padding: '12px 30px'
              }
          }
      },
      MuiSnackbar: {
          defaultProps: {
              autoHideDuration: 6000,
              anchorOrigin: {
                  horizontal: 'right',
                  vertical: 'bottom'
              }
          }
      },
      MuiTableCell: {
          styleOverrides: {
              root: {
                  whiteSpace: 'nowrap'
              }
          }
      },
      MuiDialogTitle: {
          styleOverrides: {
              root: {
                  padding: '20px 24px'
              }
          }
      },
      MuiFormHelperText: {
          styleOverrides: {
              root: {
                  margin: '8px 0px 0px 0px'
              }
          }
      },
      MuiStack: {
          defaultProps: {
              spacing: 0
          },
          variants: [
              {
                  props: { direction: 'column' },
                  style: { maxWidth: '100%' }
              }
          ]
      },
      MuiDivider: {
          styleOverrides: {
              root: {
                  borderColor: 'var(--color-lightGrey)'
              }
          }
      },
      MuiTypography: {
          variants: [
              {
                  props: { color: 'success' },
                  style: { color: 'var(--color-success)' }
              },
              {
                  props: { color: 'warning' },
                  style: { color: 'var(--color-warning)' }
              },
              {
                  props: { color: 'tertiary' },
                  style: { color: 'var(--color-tertiary)' }
              },
              {
                  props: { color: 'light' },
                  style: { color: 'var(--color-light)' }
              },
              {
                  props: { color: 'lightGrey' },
                  style: { color: 'var(--color-lightGrey)' }
              },
              {
                  props: { variant: 'caption' },
                  style: { color: 'var(--color-text-disabled)', fontStyle: 'italic' }
              },
              {
                  props: { variant: 'body3' },
                  style: { fontSize: '0.75rem', lineHeight: '1.3' }
              }
          ]
      },
      MuiStepConnector: {
          styleOverrides: {
              root: {
                  top: '23px'
              },
              line: {
                  borderTopWidth: '4px'
              }
          }
      }
  }
}